import { useRef, useState } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Star, ArrowRight, MapPin, CheckCircle2, XCircle, Leaf } from "lucide-react";
import { checkZip, scrollToId, PROMO } from "@/lib/site";

const HERO_IMG_FALLBACK =
  "https://images.unsplash.com/photo-1606796913825-2b02883605e9?crop=entropy&cs=srgb&fm=jpg&q=85";

const WordLine = ({ words, delay = 0, className = "" }) => (
  <span className={`block ${className}`}>
    {words.split(" ").map((w, i) => (
      <span key={i} className="inline-block overflow-hidden pb-1 align-bottom">
        <motion.span
          className="inline-block"
          initial={{ y: "115%", opacity: 0 }}
          animate={{ y: "0%", opacity: 1 }}
          transition={{ duration: 0.7, delay: delay + i * 0.14, ease: [0.22, 1, 0.36, 1] }}
        >
          {w}&nbsp;
        </motion.span>
      </span>
    ))}
  </span>
);

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], [0, 140]);
  const bgY = useTransform(scrollYProgress, [0, 1], [0, 220]);

  const [zip, setZip] = useState("");
  const [result, setResult] = useState(null);

  const handleCheck = (e) => {
    e.preventDefault();
    const city = checkZip(zip.trim());
    setResult(city ? { ok: true, city } : { ok: false });
    if (city) window.dispatchEvent(new CustomEvent("oxford:set-city", { detail: city }));
  };

  return (
    <section ref={ref} data-testid="hero-section" className="grain relative overflow-hidden bg-brand-navy text-white">
      <div className="animate-hero-gradient absolute inset-0" aria-hidden />
      <motion.img
        src="/images/fabric_3d.webp"
        alt=""
        aria-hidden
        style={{ y: bgY }}
        onError={(e) => (e.currentTarget.src = "/images/fabric_3d.png")}
        className="pointer-events-none absolute inset-0 h-full w-full object-cover opacity-35"
      />
      <div className="absolute inset-0 bg-gradient-to-b from-brand-navy/70 via-brand-navy/40 to-brand-navy" />

      {/* delivery van card drives in from the left on load, then idles with a soft bob */}
      <motion.div
        data-testid="hero-van"
        initial={{ x: "-65vw", opacity: 0, rotate: -6 }}
        animate={{ x: 0, opacity: 1, rotate: -2 }}
        transition={{ type: "spring", stiffness: 38, damping: 13, delay: 1.35 }}
        className="pointer-events-none absolute bottom-4 left-0 z-[5] hidden w-48 overflow-hidden rounded-2xl border border-white/20 shadow-2xl shadow-black/50 sm:block lg:w-60"
        aria-hidden
      >
        <motion.img
          src="/images/van_cut.webp"
          onError={(e) => (e.currentTarget.src = "/images/van_cut.png")}
          alt=""
          className="w-full"
          animate={{ y: [0, -6, 0] }}
          transition={{ repeat: Infinity, duration: 4.5, ease: "easeInOut", delay: 3 }}
        />
      </motion.div>
      <Leaf className="animate-float-soft absolute left-[8%] top-40 hidden h-8 w-8 text-fall-amber/40 lg:block" />
      <Leaf className="animate-float-soft absolute bottom-32 right-[12%] hidden h-10 w-10 text-fall-orange/30 lg:block" style={{ animationDelay: "2s" }} />

      <div className="relative z-10 mx-auto grid max-w-7xl items-center gap-14 px-4 pb-24 pt-40 sm:px-6 lg:grid-cols-[1.1fr_0.9fr] lg:pb-32 lg:pt-48">
        <div>
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.15 }}
            className="mb-6 inline-flex items-center gap-2 rounded-full border border-amber-300/30 bg-white/5 px-4 py-1.5 backdrop-blur"
          >
            <Star className="h-4 w-4 fill-fall-amber text-fall-amber" />
            <span className="text-xs font-bold uppercase tracking-[0.2em] text-amber-200">
              Veteran &amp; Family-Owned · Chandler, AZ
            </span>
          </motion.div>

          <h1 data-testid="hero-headline" className="font-display text-4xl font-extrabold leading-[1.05] tracking-tight sm:text-5xl lg:text-6xl">
            <WordLine words="Clean clothes." delay={0.25} />
            <WordLine words="Zero errands." delay={0.55} />
            <WordLine words="Free delivery." delay={0.85} className="text-fall-amber" />
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.85 }}
            className="mt-6 max-w-xl text-base leading-relaxed text-slate-300"
          >
            Oxford Cleaners picks up, deep cleans, and delivers back fresh — free — across Chandler,
            Gilbert, Tempe, Ahwatukee, Sun Lakes &amp; <span className="font-bold text-amber-300">Maricopa</span>. Comforters, dry cleaning, wash &amp;
            fold and more.
          </motion.p>

          <motion.form
            onSubmit={handleCheck}
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 1.0 }}
            className="mt-8 flex max-w-md items-center gap-2 rounded-2xl border border-white/15 bg-white/5 p-2 backdrop-blur"
          >
            <MapPin className="ml-2 h-5 w-5 shrink-0 text-fall-amber" />
            <input
              data-testid="hero-zip-input"
              value={zip}
              onChange={(e) => setZip(e.target.value.replace(/\D/g, "").slice(0, 5))}
              placeholder="Enter your zip code"
              inputMode="numeric"
              className="w-full bg-transparent text-sm font-semibold text-white placeholder:text-slate-400 focus:outline-none"
            />
            <button
              data-testid="hero-zip-submit"
              type="submit"
              className="shrink-0 rounded-xl bg-brand-blue px-4 py-2.5 text-sm font-bold transition-colors hover:bg-brand-blue-hover"
            >
              Check
            </button>
          </motion.form>

          {result && (
            <motion.div
              data-testid="hero-zip-result"
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              className={`mt-3 flex flex-wrap items-center gap-3 text-sm font-semibold ${result.ok ? "text-emerald-400" : "text-rose-400"}`}
            >
              {result.ok ? (
                <>
                  <span className="flex items-center gap-2">
                    <CheckCircle2 className="h-4 w-4" /> Good news — free pickup &amp; delivery in {result.city}!
                  </span>
                  <button
                    data-testid="hero-zip-book-button"
                    type="button"
                    onClick={() => scrollToId("book")}
                    className="rounded-full bg-fall-amber px-4 py-1.5 text-xs font-extrabold uppercase tracking-widest text-brand-navy transition-transform hover:scale-105"
                  >
                    Book my {result.city} pickup →
                  </button>
                </>
              ) : (
                <>
                  <XCircle className="h-4 w-4" /> Not on a route yet — call <a href="tel:4809406023" className="underline">480-940-6023</a> and we&apos;ll try to help.
                </>
              )}
            </motion.div>
          )}

          <motion.button
            data-testid="hero-fall-special-chip"
            onClick={() => scrollToId("fall-special")}
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 1.15 }}
            className="group mt-8 inline-flex items-center gap-3 rounded-full border border-fall-amber/50 bg-fall-amber/10 px-5 py-2.5 text-sm font-bold text-amber-200 transition-colors hover:bg-fall-amber/20"
          >
            <span className="h-2 w-2 animate-pulse rounded-full bg-fall-amber" />
            {PROMO.name}: comforters $19.99 each (2+) · ends {PROMO.endsLabel}
            <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
          </motion.button>
        </div>

        <motion.div
          style={{ y: imgY }}
          initial={{ opacity: 0, scale: 0.94 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 1.1, delay: 0.5, ease: [0.22, 1, 0.36, 1] }}
          className="relative mx-auto w-full max-w-md lg:max-w-none"
        >
          <div className="overflow-hidden rounded-[2rem] border border-white/15 shadow-2xl shadow-black/50">
            <img
              data-testid="hero-image"
              src="/images/hero_comforter.webp"
              onError={(e) => (e.currentTarget.src = "/images/hero_comforter.png")}
              alt="Freshly cleaned fluffy comforters stacked under a warm spotlight"
              className="aspect-[4/5] w-full object-cover"
            />
          </div>

          <div className="absolute -left-6 -top-6 flex h-32 w-32 items-center justify-center sm:-left-10 sm:h-36 sm:w-36">
            <div className="animate-spin-slow absolute inset-0 rounded-full border-2 border-dashed border-fall-amber/70" />
            <div className="flex h-24 w-24 flex-col items-center justify-center rounded-full bg-fall-amber text-center shadow-[0_0_35px_rgba(245,158,11,0.55)] sm:h-28 sm:w-28">
              <span className="font-display text-xl font-extrabold leading-none text-brand-navy sm:text-2xl">$19.99</span>
              <span className="mt-1 text-[9px] font-bold uppercase tracking-widest text-brand-navy/80">
                {PROMO.name}
              </span>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};
